import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { findLastKey, get } from 'lodash';
import { reduxForm, Field, formValueSelector } from 'redux-form';
import { FormattedMessage, injectIntl, intlShape } from 'react-intl';
// data
import { booleanSwitch } from './data/utils';
import onEventFaqSubmit from './data/onEventFaqSubmit';
import { mapBoolToString, pageInfo } from './data/utils';
import SelectTimeFieldHOC from './data/SelectTimeFieldHOC';
import { TimeMap } from '../elements-date-time/data/time-data';
import { EVENT_FAQ_CONST, EVENT_RULES_FORM, EVENT_FAQ_ANSWERS_CONST } from '../shared-components/faq/data/constants';
import { eventFaqDefinition, formattedMiscEventFaqDefinitions } from '../shared-components/faq/data/eventFaqDefinition';
import { onEventFaqBlur, onEventFaqChange, onEventMiscItemsChange, onEventTimeChange } from './data/onEventFaqChange';
// faq component
import InfoBar from '../app-faq-components/InfoBar';
import FaqHeader from '../app-faq-components/FaqHeader';
import ToggleFieldRow from '../app-faq-components/ToggleFieldRow';
import ButtonNavigation from '../app-faq-components/ButtonNavigation';
import RadioField from '../app-main/components/reduxForms/fields/RadioField/RadioField';
import ToggleField from '../app-main/components/reduxForms/fields/ToggleField/ToggleField';
// shared component
import SelectField from '../shared-components/reduxForms/fields/SelectField/SelectField';
import TextAreaField from '../shared-components/reduxForms/fields/TextAreaField/TextAreaField';
const { HAS_QUIET_HOURS, LOUD_MUSIC_ENDS_BY, HAS_FLEXIBLE_HOURS, OTHER_EVENT_RULES, ARE_TICKETED_EVENTS_ALLOWED } = EVENT_FAQ_CONST;
const { HOURS_ADJUSTED_UPON_REQUEST, HAS_NO_FLEXIBLE_HOURS } = EVENT_FAQ_ANSWERS_CONST;
const EventRulesForm = ({
  error,
  handleSubmit,
  intl,
  page,
  quietHourRules,
  hasOtherEventRules,
  spaceId,
  accessToken,
  showErrorToast,
  ...props
}) => {
  const SelectTimeField = SelectTimeFieldHOC(intl, SelectField);
  const eventRules = get(eventFaqDefinition, 'event_rules', {});
  const header = intl.formatMessage(pageInfo(page).header);
  const Icon = pageInfo(page).icon;
  const opHoursLabel = (
    <FormattedMessage
      id="hosting.event_rules_form.view_op_hours"
      defaultMessage="View and edit your bookable hours. {opHoursLink}"
      className="text-small text-gray-600 sans-serif"
      values={{
        opHoursLink: (
          <a
            className="underline text-gray-600"
            href={`/your-space/${spaceId}/availability`}
            target="_blank"
            rel="noopener noreferrer"
          >
            Learn more
          </a>
        ),
      }}
    />
  );
  const onChangeFn = onEventFaqChange(props.dispatch, spaceId, accessToken, showErrorToast);
  const onChangeTimeFn = onEventTimeChange(props.dispatch, spaceId, accessToken, showErrorToast);
  const onChangeMiscFn = onEventMiscItemsChange(props.dispatch, spaceId, accessToken, showErrorToast);
  const onBlurFn = onEventFaqBlur(props.dispatch, spaceId, accessToken, showErrorToast);

  return (
    <form className="flex-basis-100" onSubmit={handleSubmit}>
      <FaqHeader header={header} icon={<Icon />} />
      <div style={{ marginTop: '56px' }} className="margin-xl-bottom">
        <InfoBar
          labelInfo={formattedMiscEventFaqDefinitions.event_rules_info}
        />
      </div>
      <div>
        <div className="h6 questions-section">
          <FormattedMessage
            id="hosting.event_rules_form.quiet_hours_header"
            defaultMessage="Does your space have quiet hours?*"
          />
          <div className="text-small text-gray-600 sans-serif">
            {opHoursLabel}
          </div>
        </div>
        <Field
          component={RadioField}
          name={HAS_QUIET_HOURS}
          className="flex baseline padding-xl-bottom margin-lg-top flex-wrap space-between border-bottom-gray-200"
          errorMessage={'error'}
          onChange={onChangeFn}
          options={[
            {
              id: HAS_QUIET_HOURS,
              value: 'false',
              label: intl.formatMessage(eventRules[HAS_QUIET_HOURS])
            },
            {
              id: LOUD_MUSIC_ENDS_BY,
              value: 'true',
              label: intl.formatMessage(eventRules[LOUD_MUSIC_ENDS_BY]),
              component: (
                <div style={{ zIndex: 3 }} className="relative">
                  <Field
                    component={SelectTimeField}
                    name={LOUD_MUSIC_ENDS_BY}
                    errorMessage={'error'}
                    isStartTime
                    onChange={onChangeTimeFn}
                    isDisabled={!('true' === quietHourRules)}
                  />
                </div>
              )
            }
          ]}
        />
      </div>
      <div className="text-small text-gray-600 sans-serif margin-lg-top margin-xl-bottom">
        {formattedMiscEventFaqDefinitions.loud_music_warning}
      </div>
      <div>
        <div className="h6 questions-section">
          <FormattedMessage
            id="hosting.event_rules_form.flexible_hours"
            defaultMessage="Are your bookable hours flexible? "
          />
          <div className="text-small text-gray-600 sans-serif">
            {opHoursLabel}
          </div>
        </div>
        <Field
          component={RadioField}
          name={HAS_FLEXIBLE_HOURS}
          className="flex baseline padding-xl-bottom margin-lg-top flex-wrap space-between border-bottom-gray-200"
          onChange={onChangeFn}
          options={[
            {
              id: HOURS_ADJUSTED_UPON_REQUEST,
              value: HOURS_ADJUSTED_UPON_REQUEST,
              label: intl.formatMessage(eventRules[HAS_FLEXIBLE_HOURS][HOURS_ADJUSTED_UPON_REQUEST])
            },
            {
              id: HAS_NO_FLEXIBLE_HOURS,
              value: HAS_NO_FLEXIBLE_HOURS,
              label: intl.formatMessage(eventRules[HAS_FLEXIBLE_HOURS][HAS_NO_FLEXIBLE_HOURS])
            }
          ]}
        />
      </div>
      <div>
        <h6 className="questions-section">
          <FormattedMessage
            id="hosting.event_rules_form.event_rules_header"
            defaultMessage="Event related rules and restrictions"
          />
        </h6>
        {
          Object.keys(eventRules.rules_restriction).filter(key => key !== OTHER_EVENT_RULES).map((key) => (
            <>
              <div>
                <ToggleFieldRow
                  label={eventRules.rules_restriction[key]}
                  formName={EVENT_RULES_FORM}
                  fieldName={key}
                  dispatch={props.dispatch}
                  onChange={onChangeFn}
                  labelHasAsterisk={ARE_TICKETED_EVENTS_ALLOWED === key}
                />
              </div>
              <hr />
            </>
          ))
        }
        <div className="flex space-between middle">
          <div className="flex-basis-50">
            {intl.formatMessage(eventRules.rules_restriction[OTHER_EVENT_RULES])}
          </div>
          <div>
            <Field
              name={`${OTHER_EVENT_RULES}_toggle`}
              component={ToggleField}
              onChange={onChangeMiscFn}
              firstOption={
                {
                  id: `${OTHER_EVENT_RULES}_toggle_true`,
                  value: 'true',
                  label: intl.formatMessage(booleanSwitch.yes)
                }}
              secondOption={
                {
                  id: `${OTHER_EVENT_RULES}_toggle_false`,
                  value: 'false',
                  label: intl.formatMessage(booleanSwitch.no)
                }}
            />
          </div>
        </div>
        {
          hasOtherEventRules &&
        <div className="margin-xl-top">
          <Field
            name={OTHER_EVENT_RULES}
            component={TextAreaField}
            attributes={{
              'data-testing-id': OTHER_EVENT_RULES
            }}
            onBlur={onBlurFn}
            placeholder={intl.formatMessage(eventRules.rules_restriction[OTHER_EVENT_RULES].placeholder)}
            maxLength={1000}
          />
        </div>
        }
      </div>
      <hr style={{ marginBottom: 0 }} />
      <div className="text-small text-gray-600 sans-serif margin-lg-top margin-xl-bottom">
        {formattedMiscEventFaqDefinitions.residential_warning}
      </div>
      <div className="text-danger text-small margin-xl-bottom">{error}</div>
      <ButtonNavigation {...props} spaceId={spaceId} />
    </form>
  );
};

EventRulesForm.propTypes = {
  handleSubmit: PropTypes.func,
  error: PropTypes.string,
  dispatch: PropTypes.func,
  parkingToggleFieldValue: PropTypes.string,
  intl: intlShape,
  spaceId: PropTypes.string,
  accessToken: PropTypes.string,
  showErrorToast: PropTypes.bool,
  page: PropTypes.string,
  quietHourRules: PropTypes.bool,
  hasOtherEventRules: PropTypes.bool
};

const config = {
  form: EVENT_RULES_FORM,
  onSubmit: onEventFaqSubmit
};

const selector = formValueSelector(EVENT_RULES_FORM);

function mapStateToProps (state, { eventFaq }) {
  const quietHourRules = selector(state, HAS_QUIET_HOURS);
  const hasOtherEventRules = selector(state, [`${OTHER_EVENT_RULES}_toggle`]);
  const otherEventRules = get(eventFaq, [OTHER_EVENT_RULES], '');
  const timeEndsBy = get(eventFaq, [LOUD_MUSIC_ENDS_BY], {});
  const initialValues = mapBoolToString({
    ...eventFaq,
    [LOUD_MUSIC_ENDS_BY]: parseInt(findLastKey(TimeMap, timeEndsBy), 10),
    [`${OTHER_EVENT_RULES}_toggle`]: !!otherEventRules
  });
  return {
    initialValues,
    quietHourRules,
    hasOtherEventRules: 'true' === hasOtherEventRules
  };
}

export default connect(mapStateToProps)(reduxForm(config)(injectIntl(EventRulesForm)));
